/**
 * JSONB column contracts (E09-F03-T06) — zod schemas + inferred TS types for the
 * free-form `jsonb` columns of the cloned EER schema.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * In the legacy stack the flexible columns were UNTYPED jsonb blobs written by
 * n8n code-nodes and read by the PHP/SPA front-end with ad-hoc `obj.foo ?? …`
 * access (docs/source-n8n/reference/{Project,Kickoff,Gen,SR}.md). Nothing
 * guaranteed the *shape* — a renamed key (`manual_context.text`,
 * `kickoff_enrichment`, artifact `content`) only failed at runtime, in the
 * browser. In the new stack `schema.prisma` types every such column as a bare
 * `Json` (Prisma cannot express jsonb structure in PSL), so the TS side would
 * inherit the SAME blind spot — `prisma.project.findUnique().data` is
 * `Prisma.JsonValue` and reading `.kickoff_enrichment` off it is an `any`-shaped
 * cast with no compiler help.
 *
 * These contracts close that gap. They describe the STABLE, agreed-upon parts of
 * each jsonb shape (the keys both api and web rely on) as zod schemas, and derive
 * the TS types from them (`z.infer`) so the runtime validator and the static type
 * can never drift. They are deliberately PERMISSIVE about unknown keys — these
 * blobs legitimately carry AI-generated, evolving free-form content
 * (architecture.md §7: per-artifact `content`, request-driven `criteria`), so
 * every object schema is `.passthrough()`: unknown keys survive a round-trip
 * instead of being stripped/rejected. The contract is "these known keys have this
 * shape", not "only these keys exist".
 *
 * CONFORMANCE WITH PRISMA (acceptance criterion #2 — "drift caught at compile")
 * ----------------------------------------------------------------------------
 * Every inferred type here MUST be a structural subtype of a JSON value, so it is
 * assignable to/from Prisma's `Json` columns. Each shape is pinned by an enforced
 * `const _: IsJsonCompatible<T> = true` assertion (below): if a schema ever grows
 * a non-JSON-serialisable member (a `Date`, a `Map`, a function, …) the predicate
 * flips to `false` and the `= true` assignment fails `tsc`. The api side
 * (`packages/api/src/db/jsonbConformance.ts`) then asserts these same types are
 * mutually assignable with the GENERATED `Prisma.JsonValue` of the matching
 * column, so renaming/retyping a jsonb field on either side breaks `tsc`.
 *
 * SHAPES COVERED (the 6 the task names + the request sub-blobs they pull in):
 *   - projects.data                       → {@link ProjectData}
 *   - project_artifacts.content           → {@link ArtifactContent}
 *   - companies.firmographics             → {@link Firmographics}
 *   - research_requests.criteria          → {@link ResearchCriteria}   (intake, §4)
 *   - research_companies.breakdown        → {@link ScoreBreakdown}
 *   - research_companies.evidence         → {@link ResearchEvidence}
 *
 * Shared by BOTH `@eer/api` (route input/output validation, jsonb_set merges) and
 * `@eer/web` (typed reads in feature views).
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Shared leaf schemas
// ---------------------------------------------------------------------------
/** PK-QA-5 — outcome of a link scrape (`scrapeLink`): `ok` (text extracted),
 * `empty` (fetched 2xx but no extractable text), `failed` (throw/non-2xx). */
export const LINK_FETCH_STATUSES = ['ok', 'empty', 'failed'];
export const linkFetchStatusSchema = z.enum(LINK_FETCH_STATUSES);
/**
 * PK-QA-4 — outcome of upload-time text extraction from a manual-context file
 * (`extractFileText`): `ok` (text extracted), `empty` (parsed but no extractable
 * text), `failed` (parse threw / worker timeout / OOM / decode error — the upload
 * still succeeds with `{name,url}`), `unsupported` (mimetype not handled, or the
 * raw buffer exceeded the size cap → no parse attempted). Symmetric with the
 * link {@link LINK_FETCH_STATUSES} model so the UI can badge a file the same way.
 */
export const FILE_EXTRACT_STATUSES = ['ok', 'empty', 'failed', 'unsupported'];
export const fileExtractStatusSchema = z.enum(FILE_EXTRACT_STATUSES);
/** A hyperlink captured for a project (intro-email links, manual research). */
export const projectLinkSchema = z
    .object({
    url: z.string(),
    title: z.string().optional(),
    /** Fetched page text; empty until a link-scrape fills it (Links workflow). */
    content: z.string().optional(),
    /** Provenance, e.g. 'email' | 'manual'. */
    source: z.string().optional(),
    /**
     * PK-QA-5 — visible outcome of the scrape at add/refetch time, so the UI can
     * flag a link whose content could not be loaded (badge + "Повторить") BEFORE
     * a paid analysis rather than surfacing it as an LLM complaint. Absent on
     * legacy rows scraped before this field existed.
     */
    fetch_status: linkFetchStatusSchema.optional(),
    /** ISO timestamp of the last scrape attempt (PK-QA-5). */
    fetched_at: z.string().optional(),
})
    .passthrough();
/** A normalized CC contact attached to a project (`data.contact_cc`). */
export const contactCcSchema = z
    .object({
    name: z.string().optional(),
    email: z.string(),
})
    .passthrough();
// ---------------------------------------------------------------------------
// 1. projects.data  →  ProjectData
// ---------------------------------------------------------------------------
/**
 * Manual / email-derived context block (`projects.data.manual_context`).
 * Fixed nesting per legacy parity: `text` (plain intro-email body) + `links[]`
 * (docs/source-n8n/reference/Project.md: "В projects.data вложенность фиксирована:
 * manual_context.text, manual_context.links, contact_cc").
 */
export const manualContextSchema = z
    .object({
    text: z.string().optional(),
    links: z.array(projectLinkSchema).optional(),
})
    .passthrough();
/**
 * `projects.data` — the flexible per-project blob (schema.prisma:129,
 * database-schema §2). Holds the known launch-flow keys; AI/edge keys pass
 * through. `kickoff_enrichment` is merged under its own key by the Kickoff
 * workflow (jsonb_set '{kickoff_enrichment}') — its inner shape is free-form
 * AI output, so it is `JsonValue` here (the artifact carries the typed copy).
 */
export const projectDataSchema = z
    .object({
    /** Generated/edited intro email (pre-kickoff). */
    introEmail: z.unknown().optional(),
    /** Free-text kickoff meeting notes. */
    kickoffNotes: z.string().optional(),
    /** Raw kickoff transcript (truncated to ~3000 chars when fed to the LLM). */
    kickoffTranscript: z.string().optional(),
    /** Notes captured during artifact generation. */
    generation_notes: z.string().optional(),
    /** Manual context + scraped links (fixed nesting). */
    manual_context: manualContextSchema.optional(),
    /** Normalized CC list lifted off the intake email. */
    contact_cc: z.array(contactCcSchema).optional(),
    /** AI kickoff-enrichment result, merged under this key (free-form). */
    kickoff_enrichment: z.unknown().optional(),
})
    .passthrough();
// ---------------------------------------------------------------------------
// 2. project_artifacts.content  →  ArtifactContent
// ---------------------------------------------------------------------------
/**
 * `project_artifacts.content` — the per-artifact AI payload (schema.prisma:154,
 * database-schema §2.5). Each artifact `type` (analysis, intro_email,
 * job_description, kickoff_enrichment, …) has its OWN inner structure produced by
 * the generation prompt (docs/source-n8n/reference/Gen.md: "content каждого
 * артефакта = JSON-сериализация parsed[type]"). There is no single fixed shape,
 * so the contract is "a JSON object" — typed enough to forbid a bare string /
 * array at the column level (the legacy code already JSON.parse'd strings into
 * objects) while leaving the type-specific keys to the per-type readers. Use
 * {@link artifactContentSchema} to guarantee `content` is an object on write.
 */
export const artifactContentSchema = z.record(z.string(), z.unknown());
// ---------------------------------------------------------------------------
// 3. companies.firmographics  →  Firmographics
// ---------------------------------------------------------------------------
/**
 * `companies.firmographics` — enrichment attributes that did not earn a first
 * class column (schema.prisma:367, database-schema §3.1). The promoted firmo
 * fields (industry, size_band, founded_year, funding_stage, hq_*, …) live as real
 * columns; this blob carries the long tail returned by enrich adapters
 * (employee_count, revenue, tech_stack, social handles, raw provider fields).
 * Known numeric/string hints are typed; everything else passes through.
 */
export const firmographicsSchema = z
    .object({
    employeeCount: z.number().optional(),
    revenue: z.union([z.number(), z.string()]).optional(),
    revenueCurrency: z.string().optional(),
    description: z.string().optional(),
    website: z.string().optional(),
    tags: z.array(z.string()).optional(),
    /** Free-form provider attribution per field. */
    sources: z.record(z.string(), z.string()).optional(),
})
    .passthrough();
// ---------------------------------------------------------------------------
// 4. research_requests.criteria  →  ResearchCriteria  (intake, §4)
// ---------------------------------------------------------------------------
/** Client block inside the intake criteria (`criteria.client`). */
export const criteriaClientSchema = z
    .object({
    name: z.string().optional(),
    website: z.string().optional(),
    about: z.string().optional(),
    /** Add this client to the Do-Not-Solicit list. */
    addToDns: z.boolean().optional(),
})
    .passthrough();
/** Grade scope the recruiter wants returned. */
export const GRADE_SCOPES = ['A', 'A+B', 'A+B+C'];
export const gradeScopeSchema = z.enum(GRADE_SCOPES);
/** Verification strictness for the about-us / entity checks. */
export const VERIFY_STRICTNESS = ['strict', 'lenient'];
export const verifyStrictnessSchema = z.enum(VERIFY_STRICTNESS);
/** Research purpose. */
export const RESEARCH_PURPOSES = ['Sourcing', 'Market Mapping'];
export const researchPurposeSchema = z.enum(RESEARCH_PURPOSES);
/**
 * `research_requests.criteria` — the canonical Company-Research intake form
 * (schema.prisma:396, database-schema §4, architecture.md §7). The recruiter
 * fills this; the `research_criteria_normalize` stage later expands it into
 * hard_filters/soft_criteria/geo_tiles/grade_definitions and writes
 * `criteria_hash`. Every key is optional because intake is incremental (a draft
 * request may carry only a few fields); `.passthrough()` keeps any extra
 * normalize-stage keys.
 */
export const researchCriteriaSchema = z
    .object({
    purpose: researchPurposeSchema.optional(),
    client: criteriaClientSchema.optional(),
    industry: z.string().optional(),
    subCategories: z.array(z.string()).optional(),
    locations: z.array(z.string()).optional(),
    radius: z.number().optional(),
    companyType: z.array(z.string()).optional(),
    funding: z.array(z.string()).optional(),
    sizeGlobal: z.string().optional(),
    sizeLocalCap: z.number().optional(),
    foundedFrom: z.number().optional(),
    foundedTo: z.number().optional(),
    gradeScope: gradeScopeSchema.optional(),
    /** Free-text grade definitions written by the recruiter. */
    gradeA: z.string().optional(),
    gradeB: z.string().optional(),
    gradeC: z.string().optional(),
    verifyStrictness: verifyStrictnessSchema.optional(),
    /** Grade-A seed companies. */
    competitors: z.array(z.string()).optional(),
    /** Do-Not-Solicit companies / industries. */
    dnsCompanies: z.array(z.string()).optional(),
    dnsIndustries: z.array(z.string()).optional(),
    targetCount: z.number().optional(),
    freeText: z.string().optional(),
    linkedProject: z.number().nullable().optional(),
})
    .passthrough();
// ---------------------------------------------------------------------------
// 5. research_companies.breakdown  →  ScoreBreakdown
// ---------------------------------------------------------------------------
/** A single weighted criterion line in the score breakdown. */
export const scoreCriterionSchema = z
    .object({
    /** Raw 0..1 (or 0..100) match for this criterion before weighting. */
    score: z.number().optional(),
    /** Weight applied (from the active research_weight_profile). */
    weight: z.number().optional(),
    /** Short human rationale for this line. */
    note: z.string().optional(),
})
    .passthrough();
/**
 * `research_companies.breakdown` — per-criterion scoring detail behind the
 * aggregate `score`/`grade` columns (schema.prisma:430, database-schema §3.3,
 * grading ADR 2026-06-02). Keyed by criterion name (services, subIndustry, geo,
 * size, founded, industry — mirroring the weight profile, §3.6). The optional
 * `total`/`grade` echo the row's scalar columns for self-contained display.
 */
export const scoreBreakdownSchema = z
    .object({
    total: z.number().optional(),
    grade: z.string().optional(),
    /** Weighted contribution per criterion (key = criterion name). */
    criteria: z.record(z.string(), scoreCriterionSchema).optional(),
})
    .passthrough();
// ---------------------------------------------------------------------------
// 6. research_companies.evidence  →  ResearchEvidence
// ---------------------------------------------------------------------------
/** One piece of cited evidence (a source the verify/enrich stage relied on). */
export const evidenceItemSchema = z
    .object({
    url: z.string().optional(),
    title: z.string().optional(),
    /** Verbatim snippet supporting the claim. */
    snippet: z.string().optional(),
    /** Which field/claim this evidence backs (e.g. 'about_us', 'industry'). */
    field: z.string().optional(),
})
    .passthrough();
/**
 * `research_companies.evidence` — the citations the verify/disambiguate/score
 * stages gathered (schema.prisma:431, database-schema §3.3, architecture.md §7
 * "evidence-drawer"). `sources[]` is the flat citation list rendered in the
 * evidence drawer; `byField` groups them per verified claim. AI may attach more
 * keys, hence `.passthrough()`.
 */
export const researchEvidenceSchema = z
    .object({
    sources: z.array(evidenceItemSchema).optional(),
    byField: z.record(z.string(), z.array(evidenceItemSchema)).optional(),
    /** Free-text summary of why the entity was (dis)ambiguated/verified. */
    summary: z.string().optional(),
})
    .passthrough();
// ---------------------------------------------------------------------------
// Per-shape JSON-compatibility assertions (the type-level conformance half).
//
// Each line below is an ENFORCED compile-time guarantee that the inferred type
// is a valid JSON value (assignable to a Prisma `Json` column). The pattern is a
// `const _: IsJsonCompatible<T> = true` assignment: while `T` stays JSON the
// predicate is the literal type `true` and the assignment holds; the moment a
// schema above grows a non-JSON member (`Date`, `Map`, a function) the predicate
// becomes `false` and `true` is no longer assignable — `tsc` fails with TS2322
// (acceptance criterion #2, drift caught at build). NOTE: a bare
// `type _ = JsonCompatible<T>` alias does NOT do this — a `never`/`false`
// type-alias is not an error, so the `= true` value-level assertion is required.
// These consts are erased at runtime (no value flows from them).
// ---------------------------------------------------------------------------
const _projectDataIsJson = true;
const _artifactContentIsJson = true;
const _firmographicsIsJson = true;
const _researchCriteriaIsJson = true;
const _scoreBreakdownIsJson = true;
const _researchEvidenceIsJson = true;
// Keep the assertion consts live (they exist only for their `= true` annotation;
// without a reference some configs flag them unused, which would invite deletion).
void _projectDataIsJson;
void _artifactContentIsJson;
void _firmographicsIsJson;
void _researchCriteriaIsJson;
void _scoreBreakdownIsJson;
void _researchEvidenceIsJson;
/**
 * Registry of every jsonb contract keyed by `table.column`. A single object the
 * api conformance bridge and any generic validator can iterate. The keys are the
 * physical (snake_case) column paths so the mapping to `schema.prisma` is exact.
 */
export const JSONB_CONTRACTS = {
    'projects.data': projectDataSchema,
    'project_artifacts.content': artifactContentSchema,
    'companies.firmographics': firmographicsSchema,
    'research_requests.criteria': researchCriteriaSchema,
    'research_companies.breakdown': scoreBreakdownSchema,
    'research_companies.evidence': researchEvidenceSchema,
};
//# sourceMappingURL=jsonb.js.map